import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Box, useTheme, IconButton, Tooltip } from '@mui/material';
import * as d3 from 'd3';
import type { NumberValue } from 'd3';

type ChartPoint = {
    timestamp: string;
    price: number;
    clusterReturns: { [clusterId: number]: number };
    activeClusterId?: number;
    combinedReturn?: number;
    [key: string]: any;
};

type ChartDataResult = {
    chartData: ChartPoint[];
    priceDomain: [number, number];
    returnDomain: [number, number];
    logPriceDomain: [number, number];
    logReturnDomain: [number, number];
    cumulativeReturns: Record<number, number>[];
};

interface ParsedChartPoint extends ChartPoint {
    parsedTimestamp: Date;
}

interface PerformanceChartProps {
    data: ChartDataResult;
    enabledClusters: Record<number, boolean>;
    showCombinedStrategy: boolean;
    clusterIds: number[];
}

export const PerformanceChart: React.FC<PerformanceChartProps> = ({
    data,
    enabledClusters,
    showCombinedStrategy,
    clusterIds
}) => {
    const theme = useTheme();
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(800);
    const [hoverLine, setHoverLine] = useState<string | null>(null);
    const [isLogScale, setIsLogScale] = useState(false);

    const enabledClusterIds = useMemo(() => {
        return clusterIds.filter(id => enabledClusters[id]);
    }, [clusterIds, enabledClusters]);

    const hasCombinedStrategy = useMemo(() => {
        return showCombinedStrategy;
    }, [showCombinedStrategy]);

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!svgRef.current || data.chartData.length === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const margin = { top: 20, right: 50, bottom: 40, left: 50 };
        const height = 400 - margin.top - margin.bottom;
        const innerWidth = width - margin.left - margin.right;

        const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

        const parseTime = d3.timeParse('%Y-%m-%dT%H:%M:%S.%LZ');
        const parsedData: ParsedChartPoint[] = data.chartData.map(d => ({
            ...d,
            parsedTimestamp: parseTime(d.timestamp) || new Date(d.timestamp)
        }));

        const x = d3
            .scaleTime()
            .domain(d3.extent(parsedData, d => d.parsedTimestamp) as [Date, Date])
            .range([0, innerWidth]);

        const yLeft = isLogScale
            ? d3.scaleLog()
                .domain(data.logPriceDomain)
                .range([height, 0])
            : d3.scaleLinear()
                .domain(data.priceDomain)
                .range([height, 0]);

        const yRight = isLogScale
            ? d3.scaleLog()
                .domain(data.logReturnDomain)
                .range([height, 0])
            : d3.scaleLinear()
                .domain(data.returnDomain)
                .range([height, 0]);

        const clusterLineColor = (id: number) => `hsl(${(id * 50) % 360}, 90%, 80%)`;
        const clusterAreaColor = (id: number) => `hsl(${(id * 50) % 360}, 70%, 50%)`;

        const clusterLineOpacity = hasCombinedStrategy ? 0.3 : 1;

        // Grid
        const gridOpacity = 0.3;
        g.selectAll('.grid-line-vertical')
            .data(x.ticks(10))
            .join('line')
            .attr('class', 'grid-line-vertical')
            .attr('x1', d => x(d))
            .attr('x2', d => x(d))
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', theme.palette.divider)
            .attr('stroke-width', 0.5)
            .attr('stroke-dasharray', '2 2')
            .attr('stroke-opacity', gridOpacity);

        g.selectAll('.grid-line-horizontal')
            .data(yLeft.ticks(8))
            .join('line')
            .attr('class', 'grid-line-horizontal')
            .attr('x1', 0)
            .attr('x2', innerWidth)
            .attr('y1', d => yLeft(d))
            .attr('y2', d => yLeft(d))
            .attr('stroke', theme.palette.divider)
            .attr('stroke-width', 0.5)
            .attr('stroke-dasharray', '2 2')
            .attr('stroke-opacity', gridOpacity);

        // Background areas for active clusters
        clusterIds.forEach(clusterId => {
            const stepData: Array<{ start: Date, end: Date, clusterId: number }> = [];

            for (let i = 1; i < parsedData.length; i++) {
                const currentPoint = parsedData[i];
                const previousPoint = parsedData[i - 1];

                if (currentPoint.activeClusterId === clusterId) {
                    stepData.push({
                        start: previousPoint.parsedTimestamp,
                        end: currentPoint.parsedTimestamp,
                        clusterId: clusterId
                    });
                }
            }

            stepData.forEach(step => {
                g.append('rect')
                    .attr('x', x(step.start))
                    .attr('y', 0)
                    .attr('width', x(step.end) - x(step.start))
                    .attr('height', height)
                    .attr('fill', clusterAreaColor(step.clusterId))
                    .attr('fill-opacity', 0.35)
                    .attr('pointer-events', 'none');
            });
        });

        // Line generators
        const priceLine = d3
            .line<ParsedChartPoint>()
            .x(d => x(d.parsedTimestamp))
            .y(d => yLeft(d.price))
            .curve(d3.curveMonotoneX);

        // Price line (white, thick)
        g.append('path')
            .datum(parsedData)
            .attr('class', 'price-line')
            .attr('fill', 'none')
            .attr('stroke', theme.palette.text.primary)
            .attr('stroke-width', 2)
            .attr('stroke-opacity', hoverLine && hoverLine !== 'price' ? 0.1 : 1)
            .attr('d', priceLine)
            .on('mouseover', function () {
                svg.selectAll('.cluster-line, .combined-line')
                    .attr('opacity', 0.1);
                svg.selectAll('.price-line').attr('opacity', 1);
            })
            .on('mouseout', function () {
                svg.selectAll('.cluster-line, .price-line, .combined-line')
                    .attr('opacity', 1);
            });

        // Price line hover area
        g.append('path')
            .datum(parsedData)
            .attr('class', 'price-hover')
            .attr('fill', 'none')
            .attr('stroke', 'transparent')
            .attr('stroke-width', 8)
            .attr('d', priceLine)
            .on('mouseover', () => setHoverLine('price'))
            .on('mouseout', () => setHoverLine(null));

        const priceArea = d3.area<ParsedChartPoint>()
            .x(d => x(d.parsedTimestamp))
            .y0(height)
            .y1(d => yLeft(d.price))
            .curve(d3.curveMonotoneX);

        g.append('path')
            .datum(parsedData)
            .attr('fill', 'rgba(255,255,255,0.05)')
            .attr('stroke', 'none')
            .attr('d', priceArea);

        // Cluster return lines
        clusterIds.forEach(clusterId => {
            const lineData = parsedData.map((point, index) => ({
                ...point,
                clusterReturn: data.cumulativeReturns[index]?.[clusterId]
            })).filter(d => d.clusterReturn !== undefined);

            // Visible cluster line
            g.append('path')
                .datum(lineData)
                .attr('class', `cluster-line-${clusterId}`)
                .attr('fill', 'none')
                .attr('stroke', clusterLineColor(clusterId))
                .attr('stroke-width', 1.5)
                .attr('stroke-dasharray', '4 2')
                .attr('stroke-opacity', (hoverLine && hoverLine !== `cluster-${clusterId}` ? 0.1 : 1) * clusterLineOpacity)
                .attr('d', d3.line<any>()
                    .x(d => x(d.parsedTimestamp))
                    .y(d => yRight(d.clusterReturn))
                    .curve(d3.curveMonotoneX)
                );

            // Invisible hover area for cluster line
            g.append('path')
                .datum(lineData)
                .attr('class', `cluster-hover-${clusterId}`)
                .attr('fill', 'none')
                .attr('stroke', 'transparent')
                .attr('stroke-width', 8)
                .attr('d', d3.line<any>()
                    .x(d => x(d.parsedTimestamp))
                    .y(d => yRight(d.clusterReturn))
                    .curve(d3.curveMonotoneX)
                )
                .on('mouseover', () => setHoverLine(`cluster-${clusterId}`))
                .on('mouseout', () => setHoverLine(null));
        });

        // Combined strategy line
        if (hasCombinedStrategy) {
            const combinedData = parsedData.filter(d => d.combinedReturn !== undefined);

            if (enabledClusterIds.length === 0) {
                // No clusters enabled - use primary color
                g.append('path')
                    .datum(combinedData)
                    .attr('class', 'combined-line')
                    .attr('fill', 'none')
                    .attr('stroke', theme.palette.primary.main)
                    .attr('stroke-width', 3)
                    .attr('stroke-opacity', hoverLine && hoverLine !== 'combined' ? 0.1 : 1)
                    .attr('d', d3.line<ParsedChartPoint>()
                        .x(d => x(d.parsedTimestamp))
                        .y(d => yRight(d.combinedReturn!))
                        .curve(d3.curveMonotoneX)
                    );
            } else {
                // Multiple clusters - draw individual line segments between points
                for (let i = 0; i < combinedData.length - 1; i++) {
                    const currentPoint = combinedData[i];
                    const nextPoint = combinedData[i + 1];

                    const isEnabledCluster = currentPoint.activeClusterId !== undefined && enabledClusters[currentPoint.activeClusterId];
                    const segmentColor = isEnabledCluster
                        ? clusterLineColor(currentPoint.activeClusterId!)
                        : theme.palette.grey[500];
                    const segmentWidth = isEnabledCluster ? 3 : 1.5;

                    g.append('path')
                        .datum([currentPoint, nextPoint])
                        .attr('class', 'combined-segment')
                        .attr('fill', 'none')
                        .attr('stroke', segmentColor)
                        .attr('stroke-width', segmentWidth)
                        .attr('stroke-opacity', hoverLine && hoverLine !== 'combined' ? 0.1 : 1)
                        .attr('d', d3.line<ParsedChartPoint>()
                            .x(d => x(d.parsedTimestamp))
                            .y(d => yRight(d.combinedReturn!))
                            .curve(d3.curveLinear)
                        );
                }
            }

            // Hover area
            g.append('path')
                .datum(combinedData)
                .attr('class', 'combined-hover')
                .attr('fill', 'none')
                .attr('stroke', 'transparent')
                .attr('stroke-width', 8)
                .attr('d', d3.line<ParsedChartPoint>()
                    .x(d => x(d.parsedTimestamp))
                    .y(d => yRight(d.combinedReturn!))
                    .curve(d3.curveMonotoneX)
                )
                .on('mouseover', () => setHoverLine('combined'))
                .on('mouseout', () => setHoverLine(null));

            // Entry and exit markers for combined strategy
            const entries: { point: ParsedChartPoint; clusterId: number }[] = [];
            const exits: { point: ParsedChartPoint; clusterId: number }[] = [];

            for (let i = 1; i < combinedData.length; i++) {
                const current = combinedData[i];
                const previous = combinedData[i - 1];

                // Entry: cluster becomes active (only if cluster is enabled)
                if (current.activeClusterId !== undefined &&
                    current.activeClusterId !== previous.activeClusterId &&
                    enabledClusters[current.activeClusterId]) {
                    entries.push({ point: current, clusterId: current.activeClusterId });
                }

                // Exit: cluster stops being active (only if cluster is enabled)
                if (previous.activeClusterId !== undefined &&
                    current.activeClusterId !== previous.activeClusterId &&
                    enabledClusters[previous.activeClusterId]) {
                    exits.push({ point: current, clusterId: previous.activeClusterId });
                }
            }

            // Entry markers (triangles pointing up from below)
            entries.forEach(({ point, clusterId }) => {
                const triangleSize = 3;
                const yPos = yRight(point.combinedReturn!);

                g.append('path')
                    .attr('d', d3.symbol().type(d3.symbolTriangle).size(triangleSize * triangleSize * 3))
                    .attr('transform', `translate(${x(point.parsedTimestamp)}, ${yPos + 8}) rotate(0)`)
                    .attr('fill', clusterLineColor(clusterId))
                    .attr('stroke', theme.palette.background.paper)
                    .attr('stroke-width', 0.5)
                    .attr('opacity', hoverLine && hoverLine !== 'combined' ? 0.3 : 1);
            });

            // Exit markers (triangles pointing down from above)
            exits.forEach(({ point, clusterId }) => {
                const triangleSize = 3;
                const yPos = yRight(point.combinedReturn!);

                g.append('path')
                    .attr('d', d3.symbol().type(d3.symbolTriangle).size(triangleSize * triangleSize * 3))
                    .attr('transform', `translate(${x(point.parsedTimestamp)}, ${yPos - 8}) rotate(180)`)
                    .attr('fill', clusterLineColor(clusterId))
                    .attr('stroke', theme.palette.background.paper)
                    .attr('stroke-width', 0.5)
                    .attr('opacity', hoverLine && hoverLine !== 'combined' ? 0.3 : 1);
            });
        }

        // Labels with fade effect
        clusterIds.forEach(clusterId => {
            const lastIndex = data.cumulativeReturns.length - 1;
            const lastReturn = data.cumulativeReturns[lastIndex]?.[clusterId];

            if (lastReturn !== undefined) {
                g.append('text')
                    .attr('class', 'cluster-label')
                    .attr('x', innerWidth - 5)
                    .attr('y', yRight(lastReturn) - 3)
                    .attr('fill', clusterLineColor(clusterId))
                    .attr('fill-opacity', (hoverLine && hoverLine !== `cluster-${clusterId}` ? 0.1 : 1) * clusterLineOpacity)
                    .attr('text-anchor', 'end')
                    .style('font-size', '10px')
                    .style('font-weight', 'bold')
                    .text(`Cluster ${clusterId}`);
            }
        });

        if (parsedData.length > 0) {
            const lastPoint = parsedData[parsedData.length - 1];

            g.append('text')
                .attr('class', 'price-label')
                .attr('x', innerWidth - 5)
                .attr('y', yLeft(lastPoint.price) - 3)
                .attr('fill', '#fff')
                .attr('fill-opacity', hoverLine && hoverLine !== 'price' ? 0.1 : 1)
                .attr('text-anchor', 'end')
                .style('font-size', '10px')
                .style('font-weight', 'bold')
                .text('Price');
        }

        if (hasCombinedStrategy) {
            const combinedData = parsedData.filter(d => d.combinedReturn !== undefined);
            if (combinedData.length > 0) {
                const lastPoint = combinedData[combinedData.length - 1];

                g.append('text')
                    .attr('class', 'combined-label')
                    .attr('x', innerWidth - 5)
                    .attr('y', yRight(lastPoint.combinedReturn!) - 3)
                    .attr('fill', theme.palette.primary.main)
                    .attr('fill-opacity', hoverLine && hoverLine !== 'combined' ? 0.1 : 1)
                    .attr('text-anchor', 'end')
                    .style('font-size', '10px')
                    .style('font-weight', 'bold')
                    .text('Combined Strategy');
            }
        }

        // Replace the left axis section (around line 280) with this:
        const leftAxis = g.append('g');

        if (isLogScale) {
            // Log scale ticks - powers of 10 plus actual data bounds
            const logTicks = [];
            const minPrice = data.logPriceDomain[0];
            const maxPrice = data.logPriceDomain[1];

            // Add powers of 10 within the domain
            for (let power = Math.floor(Math.log10(minPrice)); power <= Math.ceil(Math.log10(maxPrice)); power++) {
                const tick = Math.pow(10, power);
                if (tick >= minPrice && tick <= maxPrice) {
                    logTicks.push(tick);
                }
            }

            // Add domain bounds if they're not already included
            if (!logTicks.includes(minPrice)) logTicks.push(minPrice);
            if (!logTicks.includes(maxPrice)) logTicks.push(maxPrice);

            // Add first price if available and not already included
            if (parsedData.length > 0) {
                const firstPrice = parsedData[0].price;
                if (!logTicks.some(tick => Math.abs(tick - firstPrice) < firstPrice * 0.01)) {
                    logTicks.push(firstPrice);
                }
            }

            // Limit to 6 ticks max and sort
            logTicks.sort((a, b) => a - b);
            const limitedTicks = logTicks.length > 6 ?
                [logTicks[0], ...logTicks.slice(1, -1).filter((_, i) => i % Math.ceil((logTicks.length - 2) / 4) === 0), logTicks[logTicks.length - 1]] :
                logTicks;

            leftAxis.call(d3.axisLeft(yLeft)
                .tickValues(limitedTicks)
                .tickFormat(d => `$${(d as number).toFixed(0)}`)
            );
        } else {
            // Linear scale ticks (your existing logic)
            const customTicks = new Set<number>();
            customTicks.add(data.priceDomain[0]);
            if (parsedData.length > 0) {
                customTicks.add(parsedData[0].price);
            }
            customTicks.add(data.priceDomain[1]);

            const roundToTwoDigits = (num: number): number => {
                if (num === 0) return 0;
                const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(num))) - 1);
                return Math.round(num / magnitude) * magnitude;
            };

            const tickCount = 5;
            const range = data.priceDomain[1] - data.priceDomain[0];
            const step = range / (tickCount - 1);

            for (let i = 1; i < tickCount - 1; i++) {
                const tick = data.priceDomain[0] + step * i;
                const roundedTick = roundToTwoDigits(tick);
                const minDistance = range * 0.1;
                const existingTicks = Array.from(customTicks);
                const tooClose = existingTicks.some(existing =>
                    Math.abs(existing - roundedTick) < minDistance
                );
                if (!tooClose) {
                    customTicks.add(roundedTick);
                }
            }

            leftAxis.call(d3.axisLeft(yLeft)
                .tickValues(Array.from(customTicks).sort((a, b) => a - b))
                .tickFormat(d => `$${(d as number).toFixed(0)}`)
            );
        }

        // Replace the right axis section with this:
        if (isLogScale) {
            // Log scale return ticks
            const returnTicks: number[] = [];
            const minReturn = data.logReturnDomain[0];
            const maxReturn = data.logReturnDomain[1];

            // Powers of 10 for returns (1 = 0%, 10 = 900%, 100 = 9900%, etc.)
            for (let power = 0; power <= Math.ceil(Math.log10(maxReturn)); power++) {
                const tick = Math.pow(10, power);
                if (tick >= minReturn && tick <= maxReturn) {
                    returnTicks.push(tick);
                }
            }

            // Add intermediate ticks (2, 5 multiples of powers of 10)
            for (let power = 0; power <= Math.ceil(Math.log10(maxReturn)); power++) {
                const base = Math.pow(10, power);
                [2, 5].forEach(multiplier => {
                    const tick = base * multiplier;
                    if (tick >= minReturn && tick <= maxReturn && !returnTicks.includes(tick)) {
                        returnTicks.push(tick);
                    }
                });
            }

            // Add domain bounds
            if (!returnTicks.includes(minReturn)) returnTicks.push(minReturn);
            if (!returnTicks.includes(maxReturn)) returnTicks.push(maxReturn);

            // Sort and ensure at least 5 ticks, max 8
            returnTicks.sort((a, b) => a - b);
            let finalReturnTicks = returnTicks;

            if (returnTicks.length > 8) {
                // Keep first, last, and evenly spaced intermediate ticks
                const step = Math.ceil((returnTicks.length - 2) / 6);
                finalReturnTicks = [
                    returnTicks[0],
                    ...returnTicks.slice(1, -1).filter((_, i) => i % step === 0),
                    returnTicks[returnTicks.length - 1]
                ];
            }

            g.append('g')
                .attr('transform', `translate(${innerWidth}, 0)`)
                .call(d3.axisRight(yRight)
                    .tickValues(finalReturnTicks)
                    .tickFormat(d => `${(((d as number) - 1) * 100).toFixed(0)}%`)
                );
        } else {
            g.append('g')
                .attr('transform', `translate(${innerWidth}, 0)`)
                .call(d3.axisRight(yRight).tickFormat(d => `${(((d as number) - 1) * 100).toFixed(1)}%`));
        }

        const tickDates = x.ticks(10);
        let lastMonth = -1;
        let lastYear = -1;

        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(
                d3.axisBottom(x)
                    .tickValues(tickDates)
                    .tickFormat((domainValue: Date | NumberValue, index: number) => {
                        const date = new Date(domainValue as Date);
                        const month = date.getMonth();
                        const year = date.getFullYear();
                        let label: string;

                        if (lastYear === -1) {
                            label = d3.timeFormat('%d.%m.%Y')(date);
                        } else if (year !== lastYear) {
                            label = `${year}`;
                        } else if (month !== lastMonth) {
                            label = d3.timeFormat('%B')(date);
                        } else {
                            label = d3.timeFormat('%d.%m.%Y')(date);
                        }

                        lastMonth = month;
                        lastYear = year;
                        return label;
                    })
            );

        // Tooltip (unchanged from your original code)
        const tooltip = g.append('g')
            .attr('class', 'tooltip')
            .style('display', 'none');

        tooltip.append('rect')
            .attr('fill', 'rgba(0, 0, 0, 0.8)')
            .attr('rx', 4)
            .attr('ry', 4);

        tooltip.append('text')
            .attr('fill', 'white')
            .attr('font-size', '12px')
            .attr('text-anchor', 'start');

        g.append('rect')
            .attr('width', innerWidth)
            .attr('height', height)
            .attr('fill', 'transparent')
            .attr('pointer-events', 'all')
            .on('mousemove', function (event) {
                const [mouseX] = d3.pointer(event, this);
                const date = x.invert(mouseX);

                let closestPoint = parsedData[0];
                let minDistance = Math.abs(closestPoint.parsedTimestamp.getTime() - date.getTime());

                parsedData.forEach(point => {
                    const distance = Math.abs(point.parsedTimestamp.getTime() - date.getTime());
                    if (distance < minDistance) {
                        minDistance = distance;
                        closestPoint = point;
                    }
                });

                const tooltipLines = [`Date: ${closestPoint.parsedTimestamp.toLocaleDateString()}`];
                tooltipLines.push(`Price: $${closestPoint.price.toFixed(0)}`);

                clusterIds.forEach(id => {
                    const pointIndex = parsedData.indexOf(closestPoint);
                    const returnValue = data.cumulativeReturns[pointIndex]?.[id];
                    if (returnValue !== undefined) {
                        tooltipLines.push(`Cluster ${id}: ${((returnValue - 1) * 100).toFixed(1)}%`);
                    }
                });

                if (hasCombinedStrategy && closestPoint.combinedReturn !== undefined) {
                    tooltipLines.push(`Combined Strategy: ${((closestPoint.combinedReturn - 1) * 100).toFixed(1)}%`);
                }

                const textElement = tooltip.select('text');
                textElement.selectAll('tspan').remove();

                tooltipLines.forEach((line, i) => {
                    textElement.append('tspan')
                        .attr('x', 8)
                        .attr('y', 15 + i * 14)
                        .text(line);
                });

                const bbox = (textElement.node() as SVGTextElement).getBBox();
                tooltip.select('rect')
                    .attr('width', bbox.width + 16)
                    .attr('height', bbox.height + 16);

                const tooltipX = Math.min(mouseX + 10, innerWidth - bbox.width - 16);
                const tooltipY = Math.max(10, Math.min(event.offsetY - bbox.height - 20, height - bbox.height - 16));

                tooltip
                    .attr('transform', `translate(${tooltipX}, ${tooltipY})`)
                    .style('display', null);
            })
            .on('mouseout', () => {
                tooltip.style('display', 'none');
            });

    }, [data, clusterIds, enabledClusterIds, hasCombinedStrategy, theme, width, hoverLine, isLogScale]);

    return (
        <Box ref={containerRef} sx={{ width: '100%', overflowX: 'hidden', position: 'relative' }}>
            <Tooltip title={`Switch to ${isLogScale ? 'linear' : 'logarithmic'} scale`}>
                <IconButton
                    onClick={() => setIsLogScale(!isLogScale)}
                    sx={{
                        position: 'absolute',
                        bottom: 10,
                        right: 10,
                        zIndex: 1,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        color: theme.palette.text.primary,
                        width: 28,
                        height: 28,
                        fontSize: '12px',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.2)',
                        }
                    }}
                >
                    {isLogScale ? 'LIN' : 'LOG'}
                </IconButton>
            </Tooltip>
            <svg
                ref={svgRef}
                width={width}
                height={400}
                style={{ background: 'transparent' }}
            />
        </Box>
    );
};